import {
    Injectable,
    Injector,
    WritableSignal,
    computed,
    effect,
    inject,
    signal,
} from '@angular/core';
import { Observable, Subject, delay, filter, of, startWith, take } from 'rxjs';
import { Platform } from '@angular/cdk/platform';

import { FcmTokenService } from './fcm-token/fcm-token.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { tap } from 'lodash';

// 🔧 Déclarations pour window.postMessage (pas webkit)
declare global {
    interface Window {
        receiveMessageFromiOS?: (messageString: string) => void;
        sendFCMTokenToAngular?: (token: string) => void;
        sendMessageToiOS?: (type: string, data: any) => void;
        iOSBridge?: any;
        markAngularReady?: () => void;
        angular?: { isReady: boolean };
        debugIOSBridge?: () => any;
        testIOSCommunication?: () => boolean;
        iOSBridgeState?: any;
    }
}

export interface NotificationData {
    data: any;
    actionType: 'received' | 'clicked' | 'background' | 'message';
    url?: string;
    timestamp?: number;
}

export interface NotificationPermissionStatus {
    status: number; // UNAuthorizationStatus: 0=notDetermined, 1=denied, 2=authorized, 3=provisional, 4=ephemeral
    statusString: string;
    timestamp: number;
}

export interface PermissionChangeEvent {
    oldStatus: number;
    newStatus: number;
    oldStatusString: string;
    newStatusString: string;
    timestamp: number;
}

// ✅ NOUVEAUX: Interfaces Keep Awake
export interface KeepAwakeOptions {
    duration?: number; // Durée en secondes (0 = illimité)
    reason?: string;   // Raison de l'activation
}

export interface KeepAwakeStatus {
    isActive: boolean;
    hasTimer: boolean;
    timeRemaining?: number;
    autoDeactivateAt?: number;
    systemIdleTimerDisabled: boolean;
    timestamp: number;
}

export interface KeepAwakeResponse {
    action: 'keep_awake_activated' | 'keep_awake_deactivated' | 'keep_awake_status';
    isActive?: boolean;
    duration?: number;
    autoDeactivateAt?: number;
    status?: KeepAwakeStatus;
    timestamp: number;
}

@Injectable({
    providedIn: 'root',
})
export class NativeBridgeService {
    // Subjects pour les événements entrants de iOS
    private _fcmTokenSubject = new Subject<string>();
    private _nativeNotificationSubject = new Subject<NotificationData>();
    private _notificationPermissionChangedSubject =
        new Subject<PermissionChangeEvent>();
    private _notificationPermissionResultSubject = new Subject<boolean>();
    private _notificationPermissionStatusSubject =
        new Subject<NotificationPermissionStatus>();
    private _cameraPermissionResultSubject = new Subject<boolean>();
    private _bridgeReadySubject = new Subject<boolean>();

    // ✅ NOUVEAUX: Subjects Keep Awake
    private _keepAwakeStatusSubject = new Subject<KeepAwakeStatus>();
    private _keepAwakeResponseSubject = new Subject<KeepAwakeResponse>();

    private heartbeatSubject = new Subject<number>();
    private lastHeartbeat = signal<number>(0);
    private connectionStatus = signal<
        'connected' | 'disconnected' | 'reconnecting'
    >('disconnected');

    public readonly isConnected = computed(
        () => this.connectionStatus() === 'connected'
    );

    private _settingsActionSubject = new Subject<{
        action: string;
        success: boolean;
        reason: string;
    }>();
    private _appLifecycleSubject = new Subject<{
        event: string;
        timestamp: number;
    }>();

    // Signals pour exposer l'état et les données de manière réactive
    public readonly fcmToken: WritableSignal<string | null> = signal(null);
    public readonly notificationPermissionGranted: WritableSignal<
        boolean | null
    > = signal(null);
    public readonly notificationPermissionStatus: WritableSignal<NotificationPermissionStatus | null> =
        signal(null);
    public readonly cameraPermissionGranted: WritableSignal<boolean | null> =
        signal(null);
    public readonly lastPermissionChange: WritableSignal<PermissionChangeEvent | null> =
        signal(null);

    // ✅ NOUVEAUX: Signals Keep Awake
    public readonly keepAwakeStatus: WritableSignal<KeepAwakeStatus | null> = signal(null);
    public readonly isKeepAwakeActive: WritableSignal<boolean> = signal(false);
    public readonly keepAwakeTimeRemaining: WritableSignal<number | null> = signal(null);

    public readonly isAndroidApp = signal(false);
    // Signals dérivés
    public readonly isNotificationDenied = computed(() => {
        const status = this.notificationPermissionStatus();
        return status?.status === 1; // UNAuthorizationStatus.denied
    });

    public readonly isNotificationAuthorized = computed(() => {
        const status = this.notificationPermissionStatus();
        return status?.status === 2; // UNAuthorizationStatus.authorized
    });

    public readonly notificationStatusString = computed(() => {
        return this.notificationPermissionStatus()?.statusString || 'Inconnu';
    });

    // ✅ NOUVEAUX: Signals dérivés Keep Awake
    public readonly keepAwakeStatusText = computed(() => {
        const status = this.keepAwakeStatus();
        if (!status) return 'Statut inconnu';
        
        if (status.isActive) {
            if (status.timeRemaining && status.timeRemaining > 0) {
                const minutes = Math.floor(status.timeRemaining / 60);
                const seconds = Math.floor(status.timeRemaining % 60);
                return `Veille désactivée (${minutes}:${seconds.toString().padStart(2, '0')} restantes)`;
            }
            return 'Veille désactivée';
        }
        return 'Veille normale';
    });

    public readonly canActivateKeepAwake = computed(() => {
        return this.isRunningInWebView() && this.isConnected();
    });

    // Conversion des Subjects en Signals
    public readonly latestNativeNotification = toSignal(
        this._nativeNotificationSubject.asObservable()
    );

    public readonly isBridgeReady = toSignal(
        this._bridgeReadySubject.asObservable(),
        { initialValue: false }
    );

    public readonly permissionChanges = toSignal(
        this._notificationPermissionChangedSubject.asObservable()
    );

    public readonly lastSettingsAction = toSignal(
        this._settingsActionSubject.asObservable()
    );

    public readonly lastAppLifecycleEvent = toSignal(
        this._appLifecycleSubject.asObservable()
    );

    // ✅ NOUVEAUX: Conversion Subjects Keep Awake en Signals
    public readonly latestKeepAwakeStatus = toSignal(
        this._keepAwakeStatusSubject.asObservable()
    );

    public readonly latestKeepAwakeResponse = toSignal(
        this._keepAwakeResponseSubject.asObservable()
    );

    private injector = inject(Injector);

    platform = inject(Platform);


    public requestFCMTokenExplicitly(): void {
        console.log('🎫 [Angular] Demande explicite du token FCM...');
        if (this.isRunningInWebView()) {
            this.sendMessage('requestCurrentFCMToken', {
                source: 'angular_explicit_request',
                timestamp: Date.now(),
                attempt: 'explicit',
            });
        } else {
            console.warn('⚠️ Demande token explicite uniquement en WebView');
        }
    }

    // ✅ Détection WebView améliorée avec plusieurs méthodes
    public readonly isRunningInWebView = computed(() => {
        // // Méthode 1: Vérifier si sendMessageToiOS existe
        // if (typeof window.sendMessageToiOS === 'function') {
        //     return true;
        // }

        // // Méthode 2: Vérifier l'user agent
        // const userAgent = navigator.userAgent;
        // const isIOSWebView =
        //     /iPhone|iPad|iPod/.test(userAgent) &&
        //     !(
        //         userAgent.includes('Safari') &&
        //         !userAgent.includes('CriOS') &&
        //         !userAgent.includes('FxiOS')
        //     );

        // // Méthode 3: Vérifier d'autres indicateurs WebView
        // const hasWebViewIndicators =
        //     !window.navigator &&
        //     typeof window.receiveMessageFromiOS !== 'undefined'; // Bridge function existe

        // console.log('🔍 Détection WebView:', {
        //     hasSendMessage: typeof window.sendMessageToiOS === 'function',
        //     isIOSWebView,
        //     hasWebViewIndicators,
        //     userAgent: userAgent.substring(0, 50) + '...',
        // });

        // return isIOSWebView || hasWebViewIndicators;

         console.log('🔍 === DÉTECTION WEBVIEW ANDROID || IOS ===');
    
    // Méthode 1: Vérifier si les fonctions bridge Android existent
    const hasSendMessage = typeof window.sendMessageToiOS === 'function';

    const hasReceiveMessage = typeof window.receiveMessageFromiOS === 'function';
    const hasNativeBridge = typeof (window as any).NativeBridge !== 'undefined';
    
    console.log('🔍 Fonctions bridge:', {
        hasSendMessage,
        hasReceiveMessage,
        hasNativeBridge
    });
    
    // Méthode 2: Vérifier l'user agent Android WebView
    const userAgent = navigator.userAgent;
    console.log('🔍 User Agent:', userAgent);
    
    // Détection Android WebView spécifique
    const isAndroidWebView = 
        userAgent.includes('Android') && 
        userAgent.includes('wv') || // WebView indicator
        userAgent.includes('Version/') && userAgent.includes('Chrome/') && !userAgent.includes('Mobile Safari');

    if(isAndroidWebView) {
        this.isAndroidApp.set(true);
    }    
    
    // Détection iOS WebView (garde la logique existante)
    const isIOSWebView =
        /iPhone|iPad|iPod/.test(userAgent) &&
        !(
            userAgent.includes('Safari') &&
            !userAgent.includes('CriOS') &&
            !userAgent.includes('FxiOS')
        );
    
    // Méthode 3: Vérifier d'autres indicateurs WebView
    const hasWebViewIndicators = 
        window.location.protocol === 'file:' || // Chargé depuis file://
        (window as any).webkit !== undefined || // iOS WebKit
        (window as any).Android !== undefined; // Interface Android potentielle
    
    // Méthode 4: Vérifier l'absence de certaines APIs du navigateur
    const lacksBrowserAPIs = 
        typeof (window as any).chrome === 'undefined' && // Pas Chrome complet
        typeof (window as any).opera === 'undefined'; // Pas Opera
    
    console.log('🔍 Détection détaillée:', JSON.stringify({
        isAndroidWebView,
        isIOSWebView,
        hasWebViewIndicators,
        lacksBrowserAPIs,
        protocol: window.location.protocol,
        host: window.location.host
    }));
    
    // ✅ NOUVEAU: Détection prioritaire par fonctions bridge
    if (hasSendMessage || hasReceiveMessage || hasNativeBridge) {
        return true;
    }
    
    // ✅ NOUVEAU: Détection par user agent Android
    if (isAndroidWebView) {
        return true;
    }
    
    // Détection iOS (existante)
    if (isIOSWebView) {
        return true;
    }
    
    // Autres indicateurs
    if (hasWebViewIndicators) {
        return true;
    }
    

    return false;
    });

    constructor() {
        this.setupiOSMessageListeners();
        this.setupAngularToiOSBridge();
        this.setupSignalSubscriptions();
        this.initializePermissionMonitoring();
        this.setupHeartbeatMonitoring();
        this.setupKeepAwakeMonitoring(); // ✅ NOUVEAU

        console.log(
            '🔧 NativeBridgeService initialized with postMessage communication'
        );
    }

    // ✅ NOUVEAU: Configuration du monitoring Keep Awake
    private setupKeepAwakeMonitoring(): void {
        // Abonner les Subjects aux Signals
        this._keepAwakeStatusSubject.subscribe((status) => {
            this.keepAwakeStatus.set(status);
            this.isKeepAwakeActive.set(status.isActive);
            this.keepAwakeTimeRemaining.set(status.timeRemaining || null);
            console.log('🔋 Statut Keep Awake mis à jour:', status);
        });

        this._keepAwakeResponseSubject.subscribe((response) => {
            console.log('🔋 Réponse Keep Awake reçue:', response);
        });

        // Effet pour logger les changements de statut Keep Awake
        effect(() => {
            const isActive = this.isKeepAwakeActive();
            console.log('🔋 État Keep Awake changé:', isActive ? 'ACTIF' : 'INACTIF');
        }, { injector: this.injector });
    }

    private setupHeartbeatMonitoring(): void {
        // Écouter les heartbeats
        this.heartbeatSubject.subscribe((timestamp) => {
            this.lastHeartbeat.set(timestamp);
            this.connectionStatus.set('connected');
        });

        // Vérifier la connexion toutes les 15 secondes
        setInterval(() => {
            const now = Date.now();
            const lastBeat = this.lastHeartbeat();

            if (lastBeat && now - lastBeat > 15000) {
                console.warn(
                    '💔 Heartbeat manqué, connexion possiblement perdue'
                );
                this.connectionStatus.set('disconnected');
                this.attemptReconnection();
            }
        }, 15000);
    }

    private attemptReconnection(): void {
        if (this.connectionStatus() === 'reconnecting') return;

        this.connectionStatus.set('reconnecting');
        console.log('🔄 Tentative de reconnexion...');

        // Test de communication
        this.testCommunication();

        setTimeout(() => {
            if (this.connectionStatus() === 'reconnecting') {
                this.connectionStatus.set('disconnected');
            }
        }, 5000);
    }

    private setupSignalSubscriptions(): void {
        // Abonner les Subjects aux Signals pour qu'ils soient mis à jour
        this._fcmTokenSubject.subscribe((token) => {
            this.fcmToken.set(token);
            console.log(
                '🎫 FCM Token updated:',
                token ? `***${token.slice(-8)}` : 'null'
            );
        });

        this._notificationPermissionResultSubject.subscribe((granted) => {
            this.notificationPermissionGranted.set(granted);
            console.log('✅ Permission result updated:', granted);
        });

        this._notificationPermissionStatusSubject.subscribe((status) => {
            this.notificationPermissionStatus.set(status);
            // Mettre à jour aussi le signal simplifié
            this.notificationPermissionGranted.set(status.status === 2);
            console.log('📊 Permission status updated:', status);
        });

        this._notificationPermissionChangedSubject.subscribe((change) => {
            this.lastPermissionChange.set(change);
            this.logDetailedPermissionChange(change);
        });

        this._cameraPermissionResultSubject.subscribe((granted) => {
            this.cameraPermissionGranted.set(granted);
            console.log('📷 Camera permission updated:', granted);
        });
    }

    // NOUVEAU: Confirmer la réception du token
    private confirmTokenReceived(token: string): void {
        console.log('🎫 Envoi de confirmation de réception du token');
        this.sendMessage('tokenReceived', {
            tokenPrefix: token.substring(0, 10),
            tokenLength: token.length,
            timestamp: Date.now(),
            confirmed: true,
        });
    }

    private setupiOSMessageListeners(): void {
        // ✅ Configuration de receiveMessageFromiOS (reçoit les messages d'iOS)
        window.receiveMessageFromiOS = (messageString: string) => {
            console.log("🚨 ===== MESSAGE REÇU D'iOS =====");
            console.log('🚨 Raw message:', messageString);
            console.log('🚨 Type:', typeof messageString);

            try {
                const message = JSON.parse(messageString);
                console.log('🚨 Message parsé:', message);
                console.log('🚨 Type du message:', message.type);
                switch (message.type) {
                    case 'fcm_token':
                        console.log('🎫 ===== TOKEN FCM REÇU =====');
                        console.log(
                            '🎫 Token reçu:',
                            message.token?.substring(0, 15) + '...'
                        );
                        console.log(
                            '🎫 Longueur:',
                            message.tokenLength || 'inconnu'
                        );
                        console.log('🎫 Source:', message.source || 'inconnu');
                        console.log(
                            '🎫 Timestamp:',
                            new Date(message.timestamp * 1000).toLocaleString()
                        );

                        console.log('🎫 Emergency:', message.emergency);
                        console.log('🎫 Source:', message.source);
                        console.log('🎫 ===========================');
                        this._fcmTokenSubject.next(message.token);
                        this.fcmToken.set(message.token);
                        // Confirmer la réception
                        this.confirmTokenReceived(message.token);
                        console.log('🎫 ========================');
                        break;

                    case 'fcm_token_confirmation':
                        console.log('🎫 Confirmation de token reçue:', message);
                        break;
                        
                    case 'notification':
                        console.log(
                            '📨 Notification reçue:',
                            message.data?.actionType || 'received'
                        );
                        this._nativeNotificationSubject.next({
                            data: message.data?.data || message.data,
                            actionType: message.data?.actionType || 'received',
                            url: message.data?.url,
                            timestamp: message.timestamp || Date.now(),
                        });
                        break;

                    case 'permission_result':
                        console.log('✅ Résultat permission:', message.granted);
                        this._notificationPermissionResultSubject.next(
                            message.granted
                        );
                        // Demander le statut complet après le résultat
                        setTimeout(() => this.requestPermissionStatus(), 1000);
                        break;

                    case 'permission_status':
                        console.log(
                            '📊 Statut permission:',
                            message.statusString
                        );
                        this._notificationPermissionStatusSubject.next({
                            status: message.status,
                            statusString: message.statusString,
                            timestamp: message.timestamp,
                        });
                        break;

                    case 'permission_change':
                        console.log(
                            '🔄 Changement permission:',
                            message.oldStatusString,
                            '->',
                            message.newStatusString
                        );
                        this._notificationPermissionChangedSubject.next({
                            oldStatus: message.oldStatus,
                            newStatus: message.newStatus,
                            oldStatusString: message.oldStatusString,
                            newStatusString: message.newStatusString,
                            timestamp: message.timestamp,
                        });
                        break;

                    case 'app_lifecycle':
                        console.log('📱 Lifecycle app:', message.event);
                        this._appLifecycleSubject.next({
                            event: message.event,
                            timestamp: message.timestamp,
                        });

                        if (message.event === 'willEnterForeground') {
                            setTimeout(
                                () => this.requestPermissionStatus(),
                                1000
                            );
                        }
                        break;

                    case 'camera_permission_result':
                        console.log(
                            '📷 Résultat permission caméra:',
                            message.data?.granted || message.granted
                        );
                        this._cameraPermissionResultSubject.next(
                            message.data?.granted || message.granted
                        );
                        break;

                    // ✅ NOUVEAUX: Gestion des messages Keep Awake
                    case 'operation_success':
                        console.log('✅ Opération réussie:', message.data);
                        this.handleKeepAwakeOperationResponse(message);
                        break;

                    case 'operation_error':
                        console.log('❌ Erreur opération:', message.data);
                        this.handleKeepAwakeOperationResponse(message);
                        break;

                    case 'bridgeReady':
                        console.log('🌉 Bridge iOS prêt:', message);
                        this._bridgeReadySubject.next(true);
                        break;

                    case 'test_response':
                        console.log('🧪 Réponse test reçue:', message);
                        break;

                    case 'heartbeat':
                        this.heartbeatSubject.next(message.timestamp);
                        break;

                    default:
                        console.warn(
                            '⚠️ Type de message iOS inconnu:',
                            message.type
                        );
                }
            } catch (e) {
                console.error('❌ ERREUR PARSING MESSAGE:', e);
                console.error('❌ Message brut:', messageString);
            }
            console.log('🚨 ==============================');
        };

        // Listeners CustomEvent pour rétrocompatibilité
        this.setupLegacyEventListeners();

        // Événements de lifecycle
        document.addEventListener('DOMContentLoaded', () => {
            console.log('📄 DOMContentLoaded - Vérification WebView');
            setTimeout(() => {
                if (this.isRunningInWebView()) {
                    console.log('📄 WebView détectée - Bridge prêt');
                    this._bridgeReadySubject.next(true);
                }
            }, 500);
        });

        window.addEventListener('load', () => {
            console.log('📄 Window Load - Vérification WebView');
            setTimeout(() => {
                if (this.isRunningInWebView()) {
                    console.log('📄 WebView confirmée - Bridge prêt');
                    this._bridgeReadySubject.next(true);
                }
            }, 1000);
        });

        // Écouter les changements de visibilité
        document.addEventListener('visibilitychange', () => {
            if (!document.hidden && this.isRunningInWebView()) {
                console.log('👁️ Page visible - Vérification permissions');
                setTimeout(() => this.requestPermissionStatus(), 500);
                // ✅ NOUVEAU: Vérifier aussi le statut Keep Awake
                setTimeout(() => this.requestKeepAwakeStatus(), 600);
            }
        });
    }

    // ✅ NOUVEAU: Handler pour les réponses d'opérations Keep Awake
    private handleKeepAwakeOperationResponse(message: any): void {
        if (!message.data) return;

        const action = message.data.action;
        if (!action || !action.includes('keep_awake')) return;

        console.log('🔋 ===== RÉPONSE KEEP AWAKE =====');
        console.log('🔋 Action:', action);
        console.log('🔋 Succès:', message.type === 'operation_success');
        console.log('🔋 Data:', message.data);

        // Construire la réponse Keep Awake
        const response: KeepAwakeResponse = {
            action: action as any,
            isActive: message.data.isActive,
            duration: message.data.duration,
            autoDeactivateAt: message.data.autoDeactivateAt,
            status: message.data.status,
            timestamp: message.data.timestamp || Date.now()
        };

        this._keepAwakeResponseSubject.next(response);

        // Mettre à jour le statut si disponible
        if (message.data.status) {
            this._keepAwakeStatusSubject.next(message.data.status);
        } else if (action === 'keep_awake_status' && message.data.status) {
            this._keepAwakeStatusSubject.next(message.data.status);
        } else {
            // Créer un statut basique basé sur la réponse
            const basicStatus: KeepAwakeStatus = {
                isActive: message.data.isActive || false,
                hasTimer: !!message.data.duration,
                timeRemaining: message.data.timeRemaining,
                autoDeactivateAt: message.data.autoDeactivateAt,
                systemIdleTimerDisabled: message.data.isActive || false,
                timestamp: message.data.timestamp || Date.now()
            };
            this._keepAwakeStatusSubject.next(basicStatus);
        }

        console.log('🔋 ==============================');
    }

    public testCommunicationUrgent(): void {
        console.log('🧪 TEST COMMUNICATION URGENT');
        console.log(
            '🧪 window.receiveMessageFromiOS exists:',
            typeof window.receiveMessageFromiOS
        );

        // Test en simulant un message
        if (window.receiveMessageFromiOS) {
            console.log("🧪 Simulation d'un message de test...");
            window.receiveMessageFromiOS(
                '{"type":"test_from_angular","timestamp":' + Date.now() + '}'
            );
        }
    }

    private setupLegacyEventListeners(): void {
        // Garder les listeners pour la rétrocompatibilité
        window.addEventListener(
            'FCMTokenReceived',
            (event: CustomEvent<{ token: string }>) => {
                console.log(
                    '🎫 FCM Token via CustomEvent (legacy):',
                    event.detail.token?.substring(0, 10) + '...'
                );
                this._fcmTokenSubject.next(event.detail.token);
            }
        );

        // Autres listeners legacy...
    }

    // ✅ Communication avec iOS via postMessage (envoie les messages vers iOS)
    private setupAngularToiOSBridge(): void {
        window.sendMessageToiOS = (type: string, data: any) => {
            try {
                const message = {
                    type: type,
                    data: data,
                    timestamp: Date.now(),
                    source: 'angular',
                    requestId: this.generateRequestId(),
                };

                console.log(
                    '📤 Envoi message à iOS via postMessage:',
                    type,
                    message?.toString()
                );

                // ✅ Utiliser postMessage avec origine spécifique pour iOS
                window.postMessage(message, '*');
                return true;
            } catch (e) {
                console.error('❌ Erreur envoi message iOS:', e);
                return false;
            }
        };
    }

    private generateRequestId(): string {
        return (
            'req_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5)
        );
    }

    private initializePermissionMonitoring(): void {
        // Effet pour demander le statut des permissions quand le bridge est prêt
        effect(
            () => {
                if (this.isBridgeReady()) {
                    console.log(
                        '🌉 Bridge prêt - Initialisation monitoring permissions'
                    );
                    setTimeout(() => {
                        this.requestPermissionStatus();
                        this.requestFCMToken();
                        // ✅ NOUVEAU: Demander aussi le statut Keep Awake
                        this.requestKeepAwakeStatus();
                    }, 1000);
                }
            },
            { injector: this.injector }
        );

        // Effet pour logger les changements de permissions
        effect(
            () => {
                const change = this.lastPermissionChange();
                if (change) {
                    this.sendPermissionAnalytics(change);
                }
            },
            { injector: this.injector }
        );
    }

    // ✅ NOUVELLES MÉTHODES PUBLIQUES KEEP AWAKE

    /**
     * Active le mode Keep Awake (empêche la mise en veille)
     * @param options Options de configuration
     * @returns Observable qui émet true si l'activation réussit
     */
    activateKeepAwake(options: KeepAwakeOptions = {}): Observable<boolean> {
        console.log('🔋 [API] Activation Keep Awake...', options);
        
        if (!this.isRunningInWebView()) {
            console.warn('⚠️ Keep Awake uniquement disponible en WebView');
            return of(false);
        }

        const data = {
            duration: options.duration || 0,
            reason: options.reason || 'Activé depuis Angular'
        };

        this.sendMessage('activateKeepAwake', data);

        // Retourner un Observable basé sur la réponse
        return this._keepAwakeResponseSubject.asObservable().pipe(
            filter(response => response.action === 'keep_awake_activated'),
            take(1),
            delay(100), // Petit délai pour s'assurer que la réponse est traitée
            startWith(false) // Valeur par défaut en cas de timeout
        ).pipe(
            take(1)
        ) as Observable<boolean>;
    }

    /**
     * Désactive le mode Keep Awake (réactive la mise en veille normale)
     * @returns Observable qui émet true si la désactivation réussit
     */
    deactivateKeepAwake(): Observable<boolean> {
        console.log('🔋 [API] Désactivation Keep Awake...');
        
        if (!this.isRunningInWebView()) {
            console.warn('⚠️ Keep Awake uniquement disponible en WebView');
            return of(false);
        }

        this.sendMessage('deactivateKeepAwake', {});

        return this._keepAwakeResponseSubject.asObservable().pipe(
            filter(response => response.action === 'keep_awake_deactivated'),
            take(1),
            delay(100),
            startWith(false)
        ).pipe(
            take(1)
        ) as Observable<boolean>;
    }

    /**
     * Bascule l'état du Keep Awake (active si inactif, désactive si actif)
     * @param options Options utilisées si activation
     * @returns Observable qui émet le nouvel état
     */
    toggleKeepAwake(options: KeepAwakeOptions = {}): Observable<boolean> {
        console.log('🔋 [API] Toggle Keep Awake...');
        
        const isCurrentlyActive = this.isKeepAwakeActive();
        
        if (isCurrentlyActive) {
            return this.deactivateKeepAwake();
        } else {
            return this.activateKeepAwake(options);
        }
    }

    /**
     * Demande le statut actuel du Keep Awake
     */
    requestKeepAwakeStatus(): void {
        console.log('🔋 [API] Demande statut Keep Awake...');
        
        if (this.isRunningInWebView()) {
            this.sendMessage('getKeepAwakeStatus', {});
        } else {
            console.warn('⚠️ Statut Keep Awake uniquement en WebView');
        }
    }

    /**
     * Méthodes de convénience pour des cas d'usage spécifiques
     */

    /**
     * Active Keep Awake pour une durée spécifique (par exemple pour une vidéo)
     * @param durationMinutes Durée en minutes
     * @param reason Raison de l'activation
     */
    activateKeepAwakeForDuration(durationMinutes: number, reason: string = 'Durée spécifique'): Observable<boolean> {
        return this.activateKeepAwake({
            duration: durationMinutes * 60, // Convertir en secondes
            reason: reason
        });
    }

    /**
     * Active Keep Awake pour une session de travail (2 heures par défaut)
     * @param reason Raison de la session
     */
    activateKeepAwakeForWorkSession(reason: string = 'Session de travail'): Observable<boolean> {
        return this.activateKeepAwakeForDuration(120, reason); // 2 heures
    }

    /**
     * Active Keep Awake pour une présentation (1 heure par défaut)
     * @param reason Raison de la présentation
     */
    activateKeepAwakeForPresentation(reason: string = 'Présentation'): Observable<boolean> {
        return this.activateKeepAwakeForDuration(60, reason); // 1 heure
    }

    /**
     * Active Keep Awake pour une session d'entraînement
     * @param durationMinutes Durée en minutes
     */
    activateKeepAwakeForWorkout(durationMinutes: number = 90): Observable<boolean> {
        return this.activateKeepAwakeForDuration(durationMinutes, 'Session d\'entraînement');
    }

    // ✅ MÉTHODES PUBLIQUES EXISTANTES (inchangées)

    requestFCMToken(): void {
        console.log('🎫 [API] Demande token FCM...');
        console.log('🔍 WebView détectée:', this.isRunningInWebView());

        if (this.isRunningInWebView()) {
            this.sendMessage('requestFCMToken', {});
        } else {
            console.warn('⚠️ Demande token FCM uniquement en WebView');
        }
    }

    requestNativeNotificationPermission(): Observable<boolean> {
        console.log('🔔 [API] Demande permission notifications...');
        console.log('🔍 WebView détectée:', this.isRunningInWebView());

        if (this.isRunningInWebView()) {
            this.sendMessage('requestNotificationPermission', {});

            return this._notificationPermissionResultSubject
                .asObservable()
                .pipe(take(1));
        } else {
            console.warn('⚠️ Demande permission uniquement en WebView');
            return of(false);
        }
    }

    requestPermissionStatus(): void {
        console.log('📊 [API] Demande statut permissions...');
        console.log('🔍 WebView détectée:', this.isRunningInWebView());

        if (this.isRunningInWebView()) {
            this.sendMessage('requestPermissionStatus', {});
        } else {
            console.warn('⚠️ Vérification statut uniquement en WebView');
        }
    }

    sendMessage(type: string, data: any = {}): boolean {
        if (window.sendMessageToiOS) {
            window.sendMessageToiOS(type, data);
            return true;
        }
        console.log('Message non envoyé à l\'app mobile (platform android ==>', this.isAndroidApp())
        return false;
    }

    requestNativeCameraPermission(): void {
        if (this.isRunningInWebView()) {
            console.log('📷 [API] Demande permission caméra...');
            this.sendMessage('requestCameraPermission', {});
        } else {
            console.warn('⚠️ Permission caméra uniquement en WebView');
        }
    }

    testCommunication(): void {
        console.log('🧪 [API] Test communication...');
        console.log('🔍 WebView détectée:', this.isRunningInWebView());

        if (this.isRunningInWebView()) {
            const testData = {
                testId: Math.random().toString(36).substr(2, 9),
                timestamp: Date.now(),
                userAgent: navigator.userAgent,
                url: window.location.href,
                isWebView: this.isRunningInWebView(),
                keepAwakeSupported: true // ✅ NOUVEAU: Indiquer le support Keep Awake
            };

            this.sendMessage('test', testData);
        } else {
            console.warn('⚠️ Test communication uniquement en WebView');
        }
    }

    openAppSettings(reason: string = 'notification_permission'): void {
        if (this.isRunningInWebView()) {
            console.log('⚙️ [API] Ouverture réglages iOS:', reason);
            this.sendMessage('openAppSettings', {
                reason,
                timestamp: Date.now(),
            });
        } else {
            console.warn('⚠️ Ouverture réglages uniquement en WebView');
        }
    }

    public initializeBridge(): void {
        console.log('🚀 INIT BRIDGE - MODE DEBUG');
        console.log('🚀 UserAgent:', navigator.userAgent.substring(0, 100));
        console.log('🚀 Window location:', window.location.href);

        setTimeout(() => {
            const debugInfo = {
                isRunningInWebView: this.isRunningInWebView(),
                hasSendMessage: typeof window.sendMessageToiOS === 'function',
                hasReceiveMessage:
                    typeof window.receiveMessageFromiOS === 'function',
                userAgent: navigator.userAgent.substring(0, 100),
                keepAwakeSupported: this.canActivateKeepAwake(), // ✅ NOUVEAU
            };

            console.log('🔍 DEBUG INFO:', debugInfo);

            if (this.isRunningInWebView()) {
                console.log('✅ WebView confirmée - Test communication');

                // Test immédiat
                this.testCommunicationUrgent();

                // Demandes multiples
                this.requestPermissionStatus();
                this.requestFCMToken();
                this.requestKeepAwakeStatus(); // ✅ NOUVEAU

                // Demande explicite après délai
                setTimeout(() => {
                    console.log('🎫 Demande EXPLICITE token FCM...');
                    this.sendMessage('requestCurrentFCMToken', {
                        urgent: true,
                        timestamp: Date.now(),
                    });
                }, 2000);

                // Debug iOS après délai
                setTimeout(() => {
                    console.log('🔍 Demande debug iOS...');
                    this.sendMessage('debugTokenIssue', {});
                }, 3000);
            } else {
                console.log('ℹ️ Mode web standard');
            }
        }, 1500);
    }

    // Méthodes de debug
    getDebugInfo(): any {
        const debugInfo = {
            isRunningInWebView: this.isRunningInWebView(),
            isBridgeReady: this.isBridgeReady(),
            fcmToken: this.fcmToken()
                ? `***${this.fcmToken()?.slice(-8)}`
                : null,
            notificationPermissionGranted: this.notificationPermissionGranted(),
            notificationPermissionStatus: this.notificationPermissionStatus(),
            isNotificationAuthorized: this.isNotificationAuthorized(),
            isNotificationDenied: this.isNotificationDenied(),
            notificationStatusString: this.notificationStatusString(),
            connectionStatus: this.connectionStatus(),
            lastHeartbeat: this.lastHeartbeat(),
            // ✅ NOUVEAUX: Debug info Keep Awake
            keepAwakeStatus: this.keepAwakeStatus(),
            isKeepAwakeActive: this.isKeepAwakeActive(),
            keepAwakeTimeRemaining: this.keepAwakeTimeRemaining(),
            keepAwakeStatusText: this.keepAwakeStatusText(),
            canActivateKeepAwake: this.canActivateKeepAwake(),
            bridgeFunctions: {
                sendMessageToiOS: typeof window.sendMessageToiOS,
                receiveMessageFromiOS: typeof window.receiveMessageFromiOS,
                markAngularReady: typeof window.markAngularReady,
            },
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
        };

        console.table(debugInfo);
        return debugInfo;
    }

    // ✅ NOUVELLES MÉTHODES OBSERVABLES KEEP AWAKE

    /**
     * Observable pour écouter les changements de statut Keep Awake
     */
    onKeepAwakeStatusChange(): Observable<KeepAwakeStatus> {
        return this._keepAwakeStatusSubject.asObservable();
    }

    /**
     * Observable pour écouter les réponses aux commandes Keep Awake
     */
    onKeepAwakeResponse(): Observable<KeepAwakeResponse> {
        return this._keepAwakeResponseSubject.asObservable();
    }

    // Observables pour l'écoute des événements (existants)
    onNotificationPermissionChanged(): Observable<PermissionChangeEvent> {
        return this._notificationPermissionChangedSubject.asObservable();
    }

    onNotificationPermissionStatus(): Observable<NotificationPermissionStatus> {
        return this._notificationPermissionStatusSubject.asObservable();
    }

    onNativeNotification(): Observable<NotificationData> {
        return this._nativeNotificationSubject.asObservable();
    }

    onFCMToken(): Observable<string> {
        return this._fcmTokenSubject.asObservable();
    }

    onSettingsAction(): Observable<{
        action: string;
        success: boolean;
        reason: string;
    }> {
        return this._settingsActionSubject.asObservable();
    }

    onAppLifecycle(): Observable<{ event: string; timestamp: number }> {
        return this._appLifecycleSubject.asObservable();
    }

    // Méthodes privées de gestion (existantes, inchangées)
    private logDetailedPermissionChange(change: PermissionChangeEvent): void {
        const timestamp = new Date(change.timestamp).toLocaleString();

        console.group(`🔄 CHANGEMENT PERMISSION DÉTECTÉ [${timestamp}]`);
        console.log(
            `📊 Ancien statut: ${change.oldStatusString} (${change.oldStatus})`
        );
        console.log(
            `📊 Nouveau statut: ${change.newStatusString} (${change.newStatus})`
        );
        console.log(`⏰ Timestamp: ${change.timestamp}`);

        // Déterminer le type de changement
        let changeType = 'inconnu';
        if (change.oldStatus === 0 && change.newStatus === 2) {
            changeType = 'activation_initiale';
        } else if (change.oldStatus === 0 && change.newStatus === 1) {
            changeType = 'refus_initial';
        } else if (change.oldStatus === 2 && change.newStatus === 1) {
            changeType = 'désactivation';
        } else if (change.oldStatus === 1 && change.newStatus === 2) {
            changeType = 'réactivation';
        }

        console.log(`🏷️ Type de changement: ${changeType}`);
        console.groupEnd();
    }

    private sendPermissionAnalytics(change: PermissionChangeEvent): void {
        const analyticsData = {
            event: 'notification_permission_changed',
            oldStatus: change.oldStatus,
            newStatus: change.newStatus,
            oldStatusString: change.oldStatusString,
            newStatusString: change.newStatusString,
            timestamp: change.timestamp,
            userAgent: navigator.userAgent,
            url: window.location.href,
        };

        this.sendMessage('analytics', analyticsData);
        console.log('📈 Analytics envoyées:', analyticsData);
    }

    // Méthodes utilitaires (existantes, inchangées)
    canEnableNotifications(): boolean {
        const status = this.notificationPermissionStatus();
        return status?.status !== 1;
    }

    guideUserToEnableNotifications(): Observable<boolean> {
        const status = this.notificationPermissionStatus();

        if (!status) {
            console.log('📊 Statut inconnu, demande du statut...');
            this.requestPermissionStatus();
            return of(false);
        }

        switch (status.status) {
            case 0:
                console.log('🔔 Première demande de permission...');
                return this.requestNativeNotificationPermission();
            case 1:
                console.log('⚙️ Redirection vers les réglages...');
                this.openAppSettings('notification_permission');
                return of(false);
            case 2:
                console.log('✅ Notifications déjà autorisées');
                return of(true);
            default:
                console.log('📊 Statut non géré:', status.status);
                return of(false);
        }
    }

    getNotificationGuidance(): string {
        const status = this.notificationPermissionStatus();

        if (!this.isRunningInWebView()) {
            return "Les notifications push sont disponibles uniquement dans l'application mobile.";
        }

        if (!status) {
            return 'Vérification du statut des notifications...';
        }

        switch (status.status) {
            case 0:
                return 'Appuyez sur le toggle pour activer les notifications push.';
            case 1:
                return 'Les notifications sont désactivées. Activez-les dans les réglages iOS.';
            case 2:
                return 'Notifications push activées avec succès !';
            case 3:
                return 'Notifications provisoires activées. Confirmez dans les réglages pour les garder.';
            default:
                return `Statut des notifications : ${status.statusString}`;
        }
    }

    // ✅ NOUVEAU: Guide utilisateur pour Keep Awake
    getKeepAwakeGuidance(): string {
        if (!this.isRunningInWebView()) {
            return "La gestion de la veille est disponible uniquement dans l'application mobile.";
        }

        if (!this.isConnected()) {
            return 'Connexion à l\'application en cours...';
        }

        const status = this.keepAwakeStatus();
        if (!status) {
            return 'Vérification du statut de la veille...';
        }

        if (status.isActive) {
            if (status.timeRemaining && status.timeRemaining > 0) {
                const minutes = Math.floor(status.timeRemaining / 60);
                const seconds = Math.floor(status.timeRemaining % 60);
                return `Veille désactivée pour ${minutes}:${seconds.toString().padStart(2, '0')} restantes.`;
            }
            return 'Veille désactivée. Appuyez pour réactiver la veille normale.';
        }

        return 'Veille normale. Appuyez pour empêcher la mise en veille automatique.';
    }

    // NOUVEAU: Séquence d'initialisation avec retry (existante, inchangée)
    private performInitializationSequence(): void {
        console.log("🔄 Démarrage de la séquence d'initialisation");

        // 1. Demander le statut des permissions immédiatement
        this.requestPermissionStatus();

        // 2. Demander le token FCM avec plusieurs tentatives
        this.requestFCMTokenWithRetry();
    }

    // NOUVEAU: Demande de token avec retry (existante, inchangée)
    private requestFCMTokenWithRetry(
        attempt: number = 1,
        maxAttempts: number = 5
    ): void {
        console.log(
            `🎫 Demande token FCM - Tentative ${attempt}/${maxAttempts}`
        );

        // Demande normale
        this.requestFCMToken();

        // Si on n'a pas de token après 3 secondes, demande explicite
        setTimeout(() => {
            if (!this.fcmToken() && attempt < maxAttempts) {
                console.log(`🎫 Pas de token reçu, demande explicite...`);
                this.requestFCMTokenExplicitly();

                // Retry après 5 secondes
                setTimeout(() => {
                    if (!this.fcmToken() && attempt < maxAttempts) {
                        this.requestFCMTokenWithRetry(attempt + 1, maxAttempts);
                    }
                }, 5000);
            } else if (this.fcmToken()) {
                console.log('🎫 ✅ Token FCM reçu avec succès!');
            } else {
                console.log(
                    '🎫 ❌ Échec de récupération du token après toutes les tentatives'
                );
            }
        }, 3000);
    }

    // NOUVEAU: Méthode de debug pour le token (existante, inchangée)
    public debugTokenState() {
        const tokenInfo = {
            hasToken: !!this.fcmToken(),
            tokenPrefix: this.fcmToken()?.substring(0, 15) || null,
            tokenLength: this.fcmToken()?.length || 0,
            isWebView: this.isRunningInWebView(),
            isBridgeReady: this.isBridgeReady(),
            connectionStatus: this.connectionStatus(),
            timestamp: new Date().toISOString(),
        };

        console.table(tokenInfo);
        return tokenInfo;
    }

    // NOUVEAU: Forcer une nouvelle demande de token (existante, inchangée)
    public forceTokenRefresh(): void {
        console.log('🎫 Force refresh du token FCM...');
        if (this.isRunningInWebView()) {
            // Réinitialiser le token local
            this.fcmToken.set(null);

            // Demander un nouveau token
            this.requestFCMTokenWithRetry();
        }
    }

    // ✅ NOUVEAU: Méthodes debug Keep Awake
    public debugKeepAwakeState() {
        const keepAwakeInfo = {
            isSupported: this.canActivateKeepAwake(),
            isActive: this.isKeepAwakeActive(),
            status: this.keepAwakeStatus(),
            statusText: this.keepAwakeStatusText(),
            timeRemaining: this.keepAwakeTimeRemaining(),
            isWebView: this.isRunningInWebView(),
            isConnected: this.isConnected(),
            timestamp: new Date().toISOString(),
        };

        console.table(keepAwakeInfo);
        return keepAwakeInfo;
    }

    public forceKeepAwakeStatusRefresh(): void {
        console.log('🔋 Force refresh du statut Keep Awake...');
        if (this.isRunningInWebView()) {
            this.requestKeepAwakeStatus();
        }
    }
}